import { motion } from "framer-motion";
import { ScanLine, CheckCircle2, AlertTriangle, XCircle, Store } from "lucide-react";

const ingredients = [
  { n: "Almonds", verdict: "Healthy", detail: "Good fats · 6g protein", level: "good" },
  { n: "Honey", verdict: "Healthy", detail: "Natural sweetener", level: "good" },
  { n: "Glucose syrup", verdict: "Caution", detail: "Added sugar", level: "warn" },
  { n: "BHT preservative", verdict: "Danger", detail: "Long-term concerns", level: "bad" },
];

const levelMap = {
  good: {
    chip: "bg-emerald-100 text-emerald-700",
    dot: "bg-emerald-500",
    Icon: CheckCircle2,
    iconColor: "text-emerald-500",
  },
  warn: {
    chip: "bg-amber-100 text-amber-700",
    dot: "bg-amber-500",
    Icon: AlertTriangle,
    iconColor: "text-amber-500",
  },
  bad: {
    chip: "bg-rose-100 text-rose-700",
    dot: "bg-rose-500",
    Icon: XCircle,
    iconColor: "text-rose-500",
  },
};

const reveal = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function InShopMode() {
  return (
    <section
      id="in-shop"
      className="py-20 md:py-24 bg-zoku-bg relative overflow-hidden"
      data-testid="in-shop-section"
    >
      {/* Soft glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-20 right-1/4 w-[520px] h-[520px] rounded-full bg-zoku-orangeLight blur-3xl opacity-70" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* Copy */}
        <motion.div {...reveal} className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 bg-white border border-stone-200 rounded-full px-4 py-1.5 text-xs font-semibold text-zoku-ink2">
            <Store className="w-3.5 h-3.5 text-zoku-orange" />
            In-shop mode
          </span>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-zoku-ink">
            Scan it. <span className="font-editorial text-zoku-orange">Know it.</span>
            <br />
            Then decide.
          </h2>
          <p className="mt-5 text-lg text-zoku-ink2 max-w-lg">
            Open Zoku at the aisle. Point at any barcode — food, snack, supplement, skincare — and
            get a plain-English breakdown in under two seconds.
          </p>

          <div className="mt-8 space-y-4">
            {[
              { Icon: CheckCircle2, bg: "bg-emerald-100", iconC: "text-emerald-600", t: "Healthy", d: "Whole, clean ingredients your body understands." },
              { Icon: AlertTriangle, bg: "bg-amber-100", iconC: "text-amber-600", t: "Caution", d: "OK in moderation — we flag hidden sugars, seed oils, additives." },
              { Icon: XCircle, bg: "bg-rose-100", iconC: "text-rose-600", t: "Danger", d: "Linked to health concerns. Zoku suggests a swap." },
            ].map((r, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-xl ${r.bg} flex items-center justify-center shrink-0`}>
                  <r.Icon className={`w-5 h-5 ${r.iconC}`} />
                </div>
                <div>
                  <div className="font-semibold text-zoku-ink">{r.t}</div>
                  <div className="text-sm text-zoku-ink2 max-w-sm">{r.d}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 flex items-center gap-6 text-sm">
            <div>
              <div className="font-heading text-2xl font-bold text-zoku-ink">2M+</div>
              <div className="text-xs text-zoku-ink3 uppercase tracking-wider">Products</div>
            </div>
            <div className="h-8 w-px bg-stone-300" />
            <div>
              <div className="font-heading text-2xl font-bold text-zoku-ink">1.8s</div>
              <div className="text-xs text-zoku-ink3 uppercase tracking-wider">Avg scan</div>
            </div>
            <div className="h-8 w-px bg-stone-300" />
            <div>
              <div className="font-heading text-2xl font-bold text-zoku-ink">0</div>
              <div className="text-xs text-zoku-ink3 uppercase tracking-wider">Sponsored</div>
            </div>
          </div>
        </motion.div>

        {/* Phone scanner mockup */}
        <motion.div
          {...reveal}
          transition={{ ...reveal.transition, delay: 0.15 }}
          className="lg:col-span-7 relative"
        >
          <div className="relative mx-auto max-w-[280px]">
            <div
              className="relative rounded-[2rem] bg-zoku-ink p-2 shadow-phone"
              style={{
                transform: "perspective(1800px) rotateY(-3deg)",
                transformStyle: "preserve-3d",
              }}
            >
              <div className="rounded-[1.6rem] overflow-hidden bg-white relative">
                {/* Header bar */}
                <div className="bg-zoku-ink text-white px-4 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <ScanLine className="w-3.5 h-3.5 text-zoku-orange" />
                    <span className="text-[10px] uppercase tracking-[0.2em] font-semibold">
                      Scanning…
                    </span>
                  </div>
                  <span className="text-[9px] text-white/60">9:41</span>
                </div>

                {/* Camera viewfinder */}
                <div className="relative h-36 overflow-hidden">
                  <img
                    src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=900&auto=format&fit=crop"
                    alt="Grocery shelf"
                    className="absolute inset-0 w-full h-full object-cover brightness-75"
                  />
                  {/* Scan brackets */}
                  <div className="absolute inset-10 pointer-events-none">
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-[3px] border-l-[3px] border-zoku-orange rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-[3px] border-r-[3px] border-zoku-orange rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-[3px] border-l-[3px] border-zoku-orange rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-[3px] border-r-[3px] border-zoku-orange rounded-br-lg" />
                    {/* Barcode in centre */}
                    <div className="absolute inset-x-10 top-1/2 -translate-y-1/2 bg-white/95 rounded-md py-2 px-3 shadow-lg">
                      <div className="flex items-end justify-center gap-[2px] h-8">
                        {Array.from({ length: 42 }).map((_, i) => {
                          const pattern = [2, 1, 1, 3, 1, 2, 1, 1, 3, 2, 1, 3, 1];
                          const w = pattern[i % pattern.length];
                          const heights = [72, 100, 85, 100, 92, 100, 68, 100];
                          return (
                            <div
                              key={i}
                              className="bg-zoku-ink"
                              style={{ width: `${w}px`, height: `${heights[i % heights.length]}%` }}
                            />
                          );
                        })}
                      </div>
                      <div className="mt-1 text-center text-[9px] font-mono tracking-[0.2em] text-zoku-ink">
                        5 901234 123457
                      </div>
                    </div>
                    {/* Laser sweep */}
                    <motion.div
                      initial={{ y: 0 }}
                      animate={{ y: [0, "100%", 0] }}
                      transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute left-2 right-2 h-[2px] bg-zoku-orange shadow-[0_0_12px_2px_rgba(255,107,0,0.8)] rounded-full"
                    />
                  </div>
                </div>

                {/* Result card */}
                <div className="px-4 pt-4 pb-4">
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-xl bg-zoku-orangeLight flex items-center justify-center text-xl shrink-0">
                      🥜
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-zoku-ink3">
                        Granola bar
                      </div>
                      <div className="font-heading text-base font-bold text-zoku-ink leading-tight truncate">
                        Honey & Almond Crunch
                      </div>
                      <div className="mt-1 inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 rounded-full px-2 py-0.5 text-[10px] font-semibold">
                        <AlertTriangle className="w-3 h-3" />
                        Check more · 62
                      </div>
                    </div>
                  </div>

                  {/* Ingredient list */}
                  <div className="mt-4 space-y-1.5">
                    <div className="text-[9px] uppercase tracking-[0.2em] text-zoku-ink3 font-bold">
                      Ingredient breakdown
                    </div>
                    {ingredients.map((it, i) => {
                      const lv = levelMap[it.level];
                      return (
                        <motion.div
                          key={it.n}
                          initial={{ opacity: 0, x: 10 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: i * 0.06, duration: 0.3 }}
                          className="flex items-center gap-2.5 bg-zoku-bg rounded-lg px-2.5 py-1.5 border border-stone-100"
                        >
                          <div className={`w-2 h-2 rounded-full ${lv.dot} shrink-0`} />
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] font-semibold text-zoku-ink truncate">{it.n}</div>
                            <div className="text-[10px] text-zoku-ink2 truncate">{it.detail}</div>
                          </div>
                          <span
                            className={`shrink-0 text-[9px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5 ${lv.chip}`}
                          >
                            {it.verdict}
                          </span>
                        </motion.div>
                      );
                    })}
                  </div>

                  {/* Suggest a swap */}
                  <div className="mt-3 bg-zoku-ink text-white rounded-lg p-2.5 flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-emerald-500/20 flex items-center justify-center">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                    </div>
                    <div className="flex-1 text-[11px]">
                      <div className="font-semibold leading-tight">Try this instead</div>
                      <div className="text-white/70 text-[10px]">RXBAR · Blueberry · 91</div>
                    </div>
                    <span className="text-[10px] text-zoku-orange font-bold">SWAP →</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Floating verdict chip */}
            <motion.div
              animate={{ y: [-6, 8, -6] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="hidden md:flex absolute -top-4 -right-6 bg-white rounded-2xl shadow-widget border border-stone-100 pl-2 pr-4 py-2 items-center gap-2"
            >
              <div className="w-8 h-8 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="text-xs">
                <div className="font-semibold text-zoku-ink">3 clean swaps</div>
                <div className="text-zoku-ink3 text-[10px]">available in this aisle</div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
